import React, { useMemo, useState } from 'react';

/** ─────────────────────────────────────────────────────────────
 * Group-level compact search
 * ─────────────────────────────────────────────────────────────*/
const GroupSearch = ({ value, onChange }) => (
  <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
    <div className="input-group-prepend">
      <span className="input-group-text p-1"><i className="fas fa-search" /></span>
    </div>
    <input
      type="text"
      className="form-control"
      placeholder="Filter in group…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {value && (
      <div className="input-group-append">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => onChange('')}
          title="Clear"
        >
          <i className="fas fa-times" />
        </button>
      </div>
    )}
  </div>
);

const GroupCard = ({ group, items, index, handleAmountChange, handleRemove }) => {
  const [q, setQ] = useState('');
  const collapseId = `collapse-${index}`;

  const filteredItems = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(x =>
      x.symbol.toLowerCase().includes(s) ||
      x.path.toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <div className="card mb-1">
      <div className="card-header py-1 px-2 d-flex justify-content-between align-items-center">
        <span className="font-weight-bold small m-0">
          {group} ({filteredItems.length}/{items.length})
        </span>

        <div className="d-flex align-items-center">
          <div className="mr-2">
            <GroupSearch value={q} onChange={setQ} />
          </div>
          <i
            className="fas fa-chevron-down cursor-pointer rotate-icon collapsed"
            data-toggle="collapse"
            data-target={`#${collapseId}`}
            aria-expanded="false"
            aria-controls={collapseId}
            title="Expand/Collapse"
          />
        </div>
      </div>

      <div id={collapseId} className="collapse">
        <table className="table table-sm table-bordered mb-0">
          <thead className="thead-light">
            <tr className="small">
              <th />
              <th className="p-1">Symbol</th>
              <th className="p-1">Path</th>
              <th className="p-1">Spread</th>
              <th className="p-1" style={{ display: 'flex', flexDirection: 'column' }}>
                Amount
                <TotalAmount
                  filteredItems={filteredItems}
                  handleAmountChange={handleAmountChange}
                  group={group}
                  q={q}
                />
              </th>
              <th className="p-1 text-center" style={{ width: 60 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.length === 0 && (
              <tr>
                <td className="p-2 text-muted small" colSpan={6}>
                  <i className="fas fa-info-circle mr-1" />
                  No rows match your search in this group.
                </td>
              </tr>
            )}

            {filteredItems.map((item, index) => {
              let rowClass = '';
              if (item.isRemoved) rowClass = 'table-danger';
              else if (item.isAdded) rowClass = 'table-success';

              return (
                <tr className={`${rowClass} small`} key={item.symbol}>
                  <td className="p-1">{index + 1}</td>
                  <td className="p-1">{item.symbol}</td>
                  <td className="p-1">{item.path}</td>
                  <td className="p-1">{item.spread}</td>
                  <td className="p-1" style={{ width: 100 }}>
                    <input
                      type="number"
                      className="form-control form-control-sm p-1"
                      value={item.amount || ''}
                      onChange={(e) => handleAmountChange(item, e.target.value)}
                      placeholder="0"
                    />
                  </td>
                  <td className="p-1 text-center">
                    {
                      item.isRemoved && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger p-1"
                          onClick={() => handleRemove(item)}
                          title="Remove row"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TotalAmount = ({ handleAmountChange, group, q }) => {
  const [totalAmount, setTotalAmount] = useState();
  return (
    <input
      type="number"
      className="form-control form-control-sm p-1"
      value={totalAmount}
      onChange={(e) => {
        handleAmountChange({
          group,
          query: q ? q.toLowerCase() : q
        }, e.target.value, "bulk");
        setTotalAmount(e.target.value);
      }
      }
    />
  )
}

const SpreadTable = ({ data, handleAmountChange }) => {
  const grouped = data.reduce((acc, item) => {
    const group = item.path.split('\\')[0];
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const handleRemove = (item) => {
    handleAmountChange(item, null, 'remove');
  };

  const groupEntries = Object.entries(grouped);

  return (
    <div className="container-fluid p-1 m-0">
      {groupEntries.length === 0 && (
        <div className="alert alert-warning py-2 mb-0">
          <i className="fas fa-info-circle mr-1" />
          No groups found.
        </div>
      )}

      {groupEntries.map(([group, items], index) => (
        <GroupCard
          key={group}
          group={group}
          items={items}
          index={index}
          handleAmountChange={handleAmountChange}
          handleRemove={handleRemove}
        />
      ))}
    </div>
  );
};

export default SpreadTable;
